/**
 * Overdue Payments Tab
 *
 * Displays the paginated list of overdue payment items, either for a single
 * fund or across every fund when no fundId is given. Search and status
 * filtering run server-side, so they cover the whole list and not just the
 * page on screen.
 */

import { useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Pagination } from '../ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CheckCircle, Search } from 'lucide-react';
import { useOverduePayments } from '@/hooks/react-query/useDrawdowns';
import { formatCurrency, formatDate } from '@/utils/formatters';
import type { OverduePaymentStatus } from '@/types/drawdown';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

interface OverduePaymentsTabProps {
  fundId?: string;
}

export function OverduePaymentsTab({ fundId }: OverduePaymentsTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OverduePaymentStatus | undefined>();

  // Debounce the search so typing doesn't fire a request per keystroke
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== search) {
        setSearch(trimmed);
        setCurrentPage(1);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchInput, search]);

  const { data, isLoading } = useOverduePayments({
    fund_id: fundId,
    page: currentPage,
    page_size: pageSize,
    search: search || undefined,
    status,
  });

  const overduePayments = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;
  const hasActiveFilters = Boolean(searchInput || status);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value === 'all' ? undefined : (value as OverduePaymentStatus));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setStatus(undefined);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-3">
      {/* Filter bar: a single control-height row, no card padding around it */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Search overdue payments"
            placeholder="Search investor, fund or drawdown #..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-72 h-9 pl-8"
          />
        </div>

        <Select value={status ?? 'all'} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-40 h-9" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="h-9"
          >
            Clear
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground bg-card rounded-lg border">
          <p>Loading overdue payments...</p>
        </div>
      )}
      {!isLoading && overduePayments.length === 0 && (
        <div className="text-center py-12 text-muted-foreground bg-card rounded-lg border">
          {hasActiveFilters ? (
            <p>No overdue payments match your filters.</p>
          ) : (
            <>
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p>No overdue payments! All drawdowns are on track.</p>
            </>
          )}
        </div>
      )}
      {!isLoading && overduePayments.length > 0 && (
        <div className="bg-card rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Drawdown #</th>
                  <th className="px-4 py-3 text-left">Fund</th>
                  <th className="px-4 py-3 text-left">Investor</th>
                  <th className="px-4 py-3 text-left">Due Date</th>
                  <th className="px-4 py-3 text-left">Days Overdue</th>
                  <th className="px-4 py-3 text-right">Outstanding</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {overduePayments.map((item) => (
                  <tr
                    key={`${item.drawdown_id}-${item.investor_id}`}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                      #{item.drawdown_number}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                      {item.fund_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                      {item.investor_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatDate(item.due_date)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Badge variant="destructive">
                        {item.days_overdue} days overdue
                      </Badge>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-red-600 text-right">
                      {formatCurrency(item.outstanding_amount)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Badge
                        variant={
                          item.status === 'partial' ? 'warning' : 'secondary'
                        }
                      >
                        {item.status.toUpperCase()}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4">
            <Pagination
              current={currentPage}
              total={totalCount}
              pageSize={pageSize}
              onChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
              showSizeChanger
              pageSizeOptions={[20, 50, 100]}
            />
          </div>
        </div>
      )}
    </div>
  );
}
